import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
// --- Visual System Components ---
// Coordinate Grid - System coordinates, not decoration
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.02] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '60px 60px'
}} aria-hidden="true" data-id="element-1528" />;
// Pattern Emergence Animation - Shows patterns forming over time
const PatternEmergence = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(p => (p + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  return <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-1529">
      {/* Nodes that connect and disconnect - showing feedback loops */}
      <circle cx="300" cy="400" r="4" fill="#64748B" opacity={phase >= 1 ? 1 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1530" />
      <circle cx="600" cy="300" r="4" fill="#64748B" opacity={phase >= 2 ? 1 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1531" />
      <circle cx="900" cy="400" r="4" fill="#64748B" opacity={phase >= 3 ? 1 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1532" />

      {/* Connecting lines - emerge sequentially */}
      <path d="M300 400 L600 300" stroke="#64748B" strokeWidth="0.5" opacity={phase >= 2 ? 0.5 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1533" />
      <path d="M600 300 L900 400" stroke="#64748B" strokeWidth="0.5" opacity={phase >= 3 ? 0.5 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1534" />
      <path d="M900 400 L300 400" stroke="#64748B" strokeWidth="0.5" strokeDasharray="4 4" opacity={phase === 0 ? 0.3 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1535" />
    </svg>;
};
// --- Sections ---
// 1. HERO
const HeroSection = () => <section className="relative min-h-screen flex flex-col justify-center px-6 pt-20 pb-12 overflow-hidden bg-[#0A0A0A]" data-id="element-1536">
    <CoordinateGrid data-id="element-1537" />
    <PatternEmergence data-id="element-1538" />

    <div className="relative z-10 max-w-5xl mx-auto space-y-12" data-id="element-1539">
      {/* Headline */}
      <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.15] text-[#F5F5F5] tracking-tight" style={{
      fontFamily: 'Georgia, serif'
    }} data-id="element-1540">
        We build instruments to see human patterns.
      </h1>

      {/* Subheadline */}
      <div className="space-y-6 max-w-3xl" data-id="element-1541">
        <p className="text-xl md:text-2xl text-[#F5F5F5] leading-relaxed font-light" data-id="element-1542">
          Lazy Monk Labs is a pattern recognition company. We build systems that
          reveal the repeating structures behind how people think, decide, and
          act — at work and in life.
        </p>
      </div>

      {/* Primary CTAs */}
      <div className="pt-8 flex flex-col sm:flex-row gap-4" data-id="element-1543">
        <Link to="/reports" data-id="element-1544">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1545">
            Explore Diagnostics
          </Button>
        </Link>
        <Link to="/work-mirror" data-id="element-1546">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1547">
            Explore Work Mirror
          </Button>
        </Link>
      </div>

      {/* Small line below */}
      <div className="pt-4" data-id="element-1548">
        <p className="text-sm text-gray-600" data-id="element-1549">
          Powered by Decoder — our pattern recognition engine.
        </p>
      </div>
    </div>
  </section>;
// 2. TWO WAYS TO USE OUR SYSTEM
const TwoProductsSection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1550">
    <div className="max-w-6xl mx-auto" data-id="element-1551">
      <div className="space-y-16" data-id="element-1552">
        {/* Title */}
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1553">
          Two ways to see your patterns
        </h2>

        {/* Two equal cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1554">
          {/* LEFT - DIAGNOSTICS */}
          <div className="border border-[#2B2B2B] p-10 space-y-8 bg-[#0A0A0A]" data-id="element-1555">
            <div className="space-y-4" data-id="element-1556">
              <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-1557">
                Diagnostics
              </h3>
              <p className="text-base text-gray-400 leading-relaxed" data-id="element-1558">
                One-time, deep reports that reveal your core patterns in
                specific areas of life and work. These are precise snapshots —
                designed to show you the structure you keep repeating.
              </p>
            </div>

            <div className="space-y-3 text-sm text-gray-500" data-id="element-1559">
              <p data-id="element-1560">• Self Diagnostic</p>
              <p data-id="element-1561">• Work Diagnostic</p>
              <p data-id="element-1562">• Relationship Diagnostic</p>
              <p data-id="element-1563">• Child Diagnostic</p>
            </div>

            <div className="pt-4 border-t border-[#2B2B2B]" data-id="element-1564">
              <p className="text-xs text-gray-600" data-id="element-1565">
                One-time • Immediate insight • Shareable
              </p>
            </div>

            <div className="pt-4" data-id="element-1566">
              <Link to="/reports" data-id="element-1567">
                <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-1568">
                  Explore Diagnostics
                </Button>
              </Link>
            </div>
          </div>

          {/* RIGHT - WORK MIRROR */}
          <div className="border border-[#2B2B2B] p-10 space-y-8 bg-[#0A0A0A]" data-id="element-1569">
            <div className="space-y-4" data-id="element-1570">
              <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-1571">
                Work Mirror
              </h3>
              <p className="text-base text-gray-400 leading-relaxed" data-id="element-1572">
                A living mirror that tracks how your work patterns show up in
                real situations over time. Instead of a snapshot, this lets you
                watch the pattern in motion.
              </p>
            </div>

            <div className="space-y-3 text-sm text-gray-500" data-id="element-1573">
              <p data-id="element-1574">• Log situations, modes, and days</p>
              <p data-id="element-1575">• See recurring loops and structures</p>
              <p data-id="element-1576">• Build a long-range record of how you operate</p>
            </div>

            <div className="pt-4 border-t border-[#2B2B2B]" data-id="element-1577">
              <p className="text-xs text-gray-600" data-id="element-1578">
                Ongoing • Long-term • Builds over time
              </p>
            </div>

            <div className="pt-4" data-id="element-1579">
              <Link to="/work-mirror" data-id="element-1580">
                <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-1581">
                  Explore Work Mirror
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 3. DECODER (THE ENGINE)
const DecoderEngineSection = () => <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1582">
    <div className="max-w-4xl mx-auto" data-id="element-1583">
      <div className="space-y-12" data-id="element-1584">
        <h2 className="text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1585">
          Everything is powered by Decoder
        </h2>

        <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-1586">
          <p data-id="element-1587">
            Decoder is our pattern recognition engine. It is not psychology. Not
            coaching. Not journaling. It is a system for detecting structural
            patterns in human behavior.
          </p>

          <p data-id="element-1588">Decoder powers both:</p>

          <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4" data-id="element-1589">
            <p data-id="element-1590">• Our one-time Diagnostics</p>
            <p data-id="element-1591">• And the ongoing Work Mirror</p>
          </div>

          <p className="pt-6 border-t border-[#2B2B2B] text-[#F5F5F5]" data-id="element-1592">
            It is the same engine, applied in two time horizons:
            <br data-id="element-1593" />
            <br data-id="element-1594" />
            Snapshots.
            <br data-id="element-1595" />
            And living systems.
          </p>
        </div>

        <div className="pt-8" data-id="element-1596">
          <Link to="/decoder" className="inline-flex items-center gap-3 text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-1597">
            <span data-id="element-1598">Learn about Decoder</span>
            <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-1599">
              →
            </span>
          </Link>
        </div>
      </div>
    </div>
  </section>;
// 4. WHAT WE ARE NOT
const WhatWeAreNotSection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1600">
    <div className="max-w-6xl mx-auto" data-id="element-1601">
      <div className="space-y-16" data-id="element-1602">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1603">
          This is not self-help.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1604">
          <div className="space-y-4 text-center" data-id="element-1605">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1606">Not Therapy</h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1607">
              We don't work with feelings or stories. We work with structure.
            </p>
          </div>

          <div className="space-y-4 text-center" data-id="element-1608">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1609">
              Not Productivity
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1610">
              We don't optimize tasks. We observe patterns.
            </p>
          </div>

          <div className="space-y-4 text-center" data-id="element-1611">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1612">Not Advice</h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1613">
              We don't tell you what to do. We show you what you keep doing.
            </p>
          </div>
        </div>

        <div className="pt-8 text-center" data-id="element-1614">
          <p className="text-lg text-[#F5F5F5] leading-relaxed" data-id="element-1615">
            Our job is not to change you. Our job is to make the pattern
            visible.
          </p>
        </div>
      </div>
    </div>
  </section>;
// 5. HOW PEOPLE USE THIS
const HowPeopleUseSection = () => <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1616">
    <div className="max-w-6xl mx-auto" data-id="element-1617">
      <div className="space-y-16" data-id="element-1618">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1619">
          How people use Lazy Monk Labs
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1620">
          <div className="space-y-6" data-id="element-1621">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1622">
              As a Snapshot
            </p>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1623">
              "I want to see my pattern in my work / relationship / life."
            </p>
            <p className="text-base text-[#F5F5F5]" data-id="element-1624">→ Use Diagnostics</p>
          </div>

          <div className="space-y-6" data-id="element-1625">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1626">
              As a Mirror
            </p>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1627">
              "I want to watch how this pattern plays out over time."
            </p>
            <p className="text-base text-[#F5F5F5]" data-id="element-1628">→ Use Work Mirror</p>
          </div>

          <div className="space-y-6" data-id="element-1629">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1630">
              As a System
            </p>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1631">
              "I want a long-term record of how I actually operate."
            </p>
            <p className="text-base text-[#F5F5F5]" data-id="element-1632">→ Use both</p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 6. PHILOSOPHY / CREDIBILITY
const PhilosophySection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1633">
    <div className="max-w-4xl mx-auto" data-id="element-1634">
      <div className="space-y-12" data-id="element-1635">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1636">
          Built for people who care about how they operate
        </h2>

        <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-1637">
          <p data-id="element-1638">
            Lazy Monk Labs is building a new category: pattern recognition
            instruments for human systems.
          </p>

          <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4" data-id="element-1639">
            <p className="text-gray-500" data-id="element-1640">We believe:</p>
            <p data-id="element-1641">• Most problems repeat.</p>
            <p data-id="element-1642">• Most people repeat.</p>
            <p data-id="element-1643">• But nobody can see the loop while they are inside it.</p>
          </div>

          <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-1644">
            We build mirrors for that.
          </p>
        </div>
      </div>
    </div>
  </section>;
// 7. FINAL CTA
const FinalCTASection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1645">
    <div className="max-w-4xl mx-auto text-center space-y-12" data-id="element-1646">
      <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1647">
        <Link to="/reports" data-id="element-1648">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-12 py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1649">
            Explore Diagnostics
          </Button>
        </Link>
        <Link to="/work-mirror" data-id="element-1650">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-12 py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1651">
            Explore Work Mirror
          </Button>
        </Link>
      </div>

      <div data-id="element-1652">
        <Link to="/decoder" className="inline-flex items-center gap-3 text-sm text-gray-600 hover:text-gray-400 transition-colors group" data-id="element-1653">
          <span data-id="element-1654">Or learn how the engine works</span>
          <span className="group-hover:text-gray-400 transition-colors" data-id="element-1655">→</span>
        </Link>
      </div>
    </div>
  </section>;
// 8. FOOTER
const Footer = () => <footer className="py-12 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1656">
    <div className="max-w-6xl mx-auto" data-id="element-1657">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-1658">
        <div data-id="element-1659">
          <p className="text-sm text-gray-500 font-mono" data-id="element-1660">Lazy Monk Labs</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1661">
            Pattern recognition systems
          </p>
        </div>

        <div className="flex gap-8 text-sm text-gray-500" data-id="element-1662">
          <Link to="/reports" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1663">
            Diagnostics
          </Link>
          <Link to="/work-mirror" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1664">
            Work Mirror
          </Link>
          <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1665">
            Decoder
          </Link>
          <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1666">
            Careers
          </Link>
          <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1667">
            Contact
          </a>
        </div>

        <div className="text-xs text-gray-600 font-mono" data-id="element-1668">
          © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  </footer>;
// --- Main Component ---
export function Home() {
  return <main className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] selection:bg-[#64748B] selection:text-white" data-id="element-1669">
      {/* Navigation */}
      <header data-id="element-1670">
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-1671">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1672">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-1673">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1674" />
              <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-1675">
                Lazy Monk Labs
              </span>
            </Link>
            <div className="flex items-center gap-8" data-id="element-1676">
              <Link to="/reports" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-medium" data-id="element-1677">
                Diagnostics
              </Link>
              <Link to="/work-mirror" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-medium" data-id="element-1678">
                Work Mirror
              </Link>
              <Link to="/decoder" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1679">
                Decoder
              </Link>
              <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1680">
                Careers
              </Link>
              <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" aria-label="Contact" data-id="element-1681">
                Contact
              </a>
            </div>
          </div>
        </nav>
      </header>

      <HeroSection data-id="element-1682" />
      <TwoProductsSection data-id="element-1683" />
      <DecoderEngineSection data-id="element-1684" />
      <WhatWeAreNotSection data-id="element-1685" />
      <HowPeopleUseSection data-id="element-1686" />
      <PhilosophySection data-id="element-1687" />
      <FinalCTASection data-id="element-1688" />
      <Footer data-id="element-1689" />
    </main>;
}